import React, { useState, useEffect, useCallback, useRef } from "react";
import Layout from "../../../components/Layout.tsx";
import Title from "../../../components/Title.tsx";
import {
  fetchPlantillaLideres,
  autocompletePartners,
} from "../../../services/partner.service.ts";
import { readPositions } from "../../../services/position.service.ts";
import { savePositionChange } from "../../../services/changePosition.service.ts";
import { readBranches } from "../../../services/branch.service.ts";
import { getShifts } from "../../../services/shift.service.ts";
import { configuration } from "../../reports/Configuration.ts";
import { toCamelCase } from "../../../hooks/toCameCase.tsx";
import { useAppSettings } from "../../../hooks/useAppSettings.tsx";
import { notification } from "../../../utils/Notifications.tsx";
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Avatar,
  Empty,
  Skeleton,
  Button,
  Modal,
  Input,
  Tooltip,
  Divider,
} from "antd";
import {
  PhoneOutlined,
  UserOutlined,
  TeamOutlined,
  GlobalOutlined,
  SwapOutlined,
  SearchOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Text, Title: AntTitle } = Typography;

const PlantillaLideres = () => {
  useAppSettings();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);
  const [positionsList, setPositionsList] = useState<any[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savingChange, setSavingChange] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState<any>(null);
  const [reason, setReason] = useState("Cambio táctico de líder");

  const [searchPartner, setSearchPartner] = useState("");
  const [filteredPartners, setFilteredPartners] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<any>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [branches, positions, shifts] = await Promise.all([
        readBranches(),
        readPositions(),
        getShifts(),
      ]);
      setPositionsList(positions);

      const slots: { branchId: number; positionId: number; shiftId?: number }[] =
        [];
      for (const branchConfig of configuration) {
        const branchEntity = branches.find((b) => b.name === branchConfig.branch);
        if (!branchEntity) continue;

        for (const position of branchConfig.positions) {
          const positionId = positions.find((p) => p.name === position.name)?.id;
          const shiftId = shifts.find((s: any) => s.name === position?.shift)?.id;
          if (!positionId) continue;

          slots.push({
            branchId: branchEntity.id,
            positionId,
            ...(shiftId ? { shiftId } : {}),
          });
        }
      }

      const data = await fetchPlantillaLideres(slots);

      let dataIndex = 0;
      const info: any[] = [];
      for (const branchConfig of configuration) {
        const branchEntity = branches.find((b) => b.name === branchConfig.branch);
        if (!branchEntity) continue;

        const element: any = {
          branch: branchConfig,
          branchEntity,
          positions: [],
        };

        for (const position of branchConfig.positions) {
          const positionId = positions.find((p) => p.name === position.name)?.id;
          if (!positionId) continue;

          const item = data[dataIndex++];
          element.positions.push({
            name: `${position.name}${position?.shift ? ` ${position.shift}` : ""}`,
            shift: position?.shift,
            partners: item?.partners ?? [],
            positionId: item?.positionId ?? positionId,
          });
        }
        info.push(element);
      }
      setRecords(info);
    } catch (error) {
      console.error(error);
      notification("Error", "No se pudo cargar la plantilla", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadPartners = async (searchTerm: string) => {
    try {
      setSearchLoading(true);
      const data = await autocompletePartners(searchTerm);
      setFilteredPartners(data || []);
    } catch (error) {
      setFilteredPartners([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = (val: string) => {
    setSearchPartner(val);
    setShowDropdown(true);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!val.trim()) {
      setFilteredPartners([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadPartners(val);
    }, 500);
  };

  const openChangeModal = (spotData: any, branch: any) => {
    setSelectedSpot({ ...spotData, branch });
    setIsModalOpen(true);
    setSearchPartner("");
    setSelectedPartner(null);
    setFilteredPartners([]);
  };

  const handleSaveChange = async () => {
    if (!selectedPartner) {
      notification(
        "Atención",
        "Por favor selecciona un colaborador",
        "warning",
      );
      return;
    }

    try {
      setSavingChange(true);
      const body = {
        partner: selectedPartner.id,
        newPosition: selectedSpot.positionId,
        newBranch: selectedSpot.branch.id,
        reasonForPositionChange: reason,
        isImmediate: true,
      };

      await savePositionChange(body);
      notification(
        "Éxito",
        "Solicitud de cambio enviada correctamente",
        "success",
      );
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      notification("Error", "No se pudo procesar el cambio", "danger");
    } finally {
      setSavingChange(false);
    }
  };

  // Helper to resolve Position ID
  const resolvePositionId = (key: string, branchName: string) => {
    let posName = "";

    // Mapping based on Configuration.ts and known keys
    switch (key) {
      case "lideres":
        posName = "Líder de Sucursal SUC";
        break;
      case "liderServicioMatutino":
      case "liderServicioVespertino":
        posName = "Líder de Servicio SUC";
        break;
      case "auxAdministrativoMatutino":
      case "auxAdministrativoVespertino":
        posName = "Auxiliar Administrativo SUC";
        break;
      case "chief":
        posName = "Chief Learning Officer CULTURA";
        break;
      case "jefeCultura":
        posName = "Jefe de Cultura CULTURA";
        break;
      case "entrenadorSupremo":
        posName = "Entrenador Supremo CULTURA";
        break;
      case "supervisorOperacionServicio":
        posName = "Supervisor de Operaciones en Servicio OPERACIÓN";
        break;
      case "supervisorOperacionCocina":
        posName = "Supervisor de Operaciones en Cocina OPERACIÓN";
        break;
      case "jefeInteligencia":
        posName = "Jefe de Inteligencia Comercial COMISARIATO";
        break;
      case "auxDistribucion":
        posName = "Auxiliar de Distribución COMISARIATO";
        break;
      case "jefeDistribucion":
        posName = "Jefe de Distribución COMISARIATO";
        break;
      case "jefeAlmacen":
        posName = "Jefe de Almacén COMISARIATO";
        break;
      case "jefeCompras":
        posName = "Jefe de Compras COMISARIATO";
        break;
      case "jefeProduccion":
        posName = "Jefe de Producción COMISARIATO";
        break;
      case "director":
        if (branchName === "Tecnología")
          posName = "Director de Desarrollo Tecnológico TECNOLOGÍA";
        else if (branchName === "Marketing") posName = "Director Comercial MKT";
        else if (branchName === "Administración y Finanzas")
          posName = "Director General ADMON"; // Assumption/Default
        break;
      default:
        // Try to match key directly or via camelCase logic if needed
        break;
    }

    if (!posName) return null;
    const pos = positionsList.find((p) => p.name === posName);
    return pos ? pos.id : null;
  };

  const renderPartner = (
    roleData: any,
    roleLabel: string,
    branch: any,
    roleKey: string,
  ) => {
    // If roleData is an array, it's just the partners list.
    // If it's an object, it might have {partners, positionId} (old structure?)
    // We normalize it here.
    let partners: any[] = [];
    let derivedPositionId: any = null;

    if (Array.isArray(roleData)) {
      partners = roleData;
    } else if (roleData && typeof roleData === "object") {
      partners = roleData.partners || [];
      derivedPositionId = roleData.positionId;
    }

    // Try to resolve ID if missing
    if (!derivedPositionId) {
      derivedPositionId = resolvePositionId(roleKey, branch.name);
    }

    // Also use the ID found in the first partner if available and we still don't have one
    if (
      !derivedPositionId &&
      partners.length > 0 &&
      partners[0].candidate?.vacancy?.position?.id
    ) {
      derivedPositionId = partners[0].candidate.vacancy.position.id;
    }

    const positionId = derivedPositionId;

    return (
      <div className="role-section mb-2 p-2 rounded-3 bg-light-gray position-relative shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Text
            strong
            className="text-muted"
            style={{
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {roleLabel}
          </Text>
          {positionId && (
            <Tooltip title="Cambiar">
              <Button
                type="text"
                size="small"
                shape="circle"
                icon={
                  <SwapOutlined
                    style={{ color: "#4A90E2", fontSize: "12px" }}
                  />
                }
                onClick={() =>
                  openChangeModal(
                    { positionId, positionName: roleLabel, partners },
                    branch,
                  )
                }
                className="hover-scale"
              />
            </Tooltip>
          )}
        </div>

        {!partners || partners.length === 0 ? (
          <div className="d-flex align-items-center opacity-50">
            <Avatar
              shape="square"
              size={32}
              icon={<UserOutlined />}
              style={{ backgroundColor: "#e5e7eb", color: "#9ca3af" }}
              className="rounded-2 me-2"
            />
            <Text
              type="secondary"
              style={{ fontSize: "11px", fontStyle: "italic" }}
            >
              Vacante
            </Text>
          </div>
        ) : (
          partners.map((p: any, idx: number) => (
            <div key={idx} className="d-flex align-items-center">
              <Avatar
                shape="square"
                size={36}
                src={p.urlImage}
                icon={<UserOutlined />}
                className="rounded-2 me-2 shadow-sm border border-light"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text
                  strong
                  style={{
                    color: "#111827",
                    fontSize: "12px",
                    lineHeight: "1.2",
                  }}
                  className="d-block text-truncate"
                >
                  {(() => {
                    const person = p.person || p.candidate?.person;
                    return person
                      ? toCamelCase(`${person.firstName} ${person.lastName}`)
                      : "S/N";
                  })()}
                </Text>
                <div className="d-flex align-items-center mt-0">
                  <Tag
                    color="orange"
                    className="m-0 border-0"
                    style={{
                      fontSize: "8px",
                      borderRadius: "3px",
                      lineHeight: "1.2",
                      padding: "0 4px",
                    }}
                  >
                    {(
                      p.candidate?.shift?.name ||
                      p.shift?.name ||
                      "Gral"
                    ).toUpperCase()}
                  </Tag>
                  <Text
                    type="secondary"
                    className="ms-2 text-truncate"
                    style={{ fontSize: "10px" }}
                  >
                    <PhoneOutlined
                      className="me-1"
                      style={{ fontSize: "9px" }}
                    />{" "}
                    {p.person?.phone || "S/N"}
                  </Text>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const getRoleLabel = (key: string) => {
    const labels: Record<string, string> = {
      lideres: "Líder Suc",
      liderServicioMatutino: "Lider Serv Mat",
      liderServicioVespertino: "Lider Serv Vesp",
      auxAdministrativoMatutino: "Aux Adm Mat",
      auxAdministrativoVespertino: "Aux Adm Vesp",
      director: "Director",
      jefeInteligencia: "Jefe Intel",
      auxDistribucion: "Aux Dist",
      jefeDistribucion: "Jefe Dist",
      jefeAlmacen: "Jefe Almacén",
      jefeCompras: "Jefe Compras",
      jefeProduccion: "Jefe Prod",
      chief: "Chief CLO",
      jefeCultura: "Jefe Cultura",
      entrenadorSupremo: "Entrenador",
      supervisorOperacionServicio: "Sup Serv",
      supervisorOperacionCocina: "Sup Cocina",
    };
    return labels[key] || toCamelCase(key.replace(/([A-Z])/g, " $1"));
  };

  return (
    <Layout loading={false}>
      <Title
        baseTitle="Táctica Operativa"
        basePath="/tactica-operativa"
        activeTitle="Líderes"
        title="Plantilla de Líderes"
      />

      <div className="container-fluid px-3">
        {loading ? (
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Col xs={24} sm={12} md={8} lg={6} xl={4} key={i}>
                <Card
                  style={{ borderRadius: "16px" }}
                  className="border-0 shadow-sm p-1"
                >
                  <Skeleton
                    active
                    avatar
                    paragraph={{ rows: 2 }}
                    title={false}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Row gutter={[12, 12]}>
            {records.length > 0 ? (
              records.map((record, index) => {
                const branchName = record.branch?.branch || record.branchEntity?.name || "Sucursal";
                const isSpecial = [
                  "Comisariato (Cedis)",
                  "Formación Integral y Cultura",
                  "Operación",
                  "Talento Humano",
                ].includes(branchName);

                return (
                  <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
                    <Card
                      size="small"
                      title={
                        <Space size={4}>
                          {isSpecial ? (
                            <GlobalOutlined
                              style={{ color: "#4A90E2", fontSize: "14px" }}
                            />
                          ) : (
                            <TeamOutlined
                              style={{ color: "#4A90E2", fontSize: "14px" }}
                            />
                          )}
                          <span
                            style={{
                              fontWeight: 800,
                              fontSize: "13px",
                              color: "#111827",
                            }}
                          >
                            {branchName}
                          </span>
                        </Space>
                      }
                      bordered={false}
                      className="shadow-sm card-compact"
                      style={{ borderRadius: "16px", height: "100%" }}
                    >
                      {record.positions?.map((pos: any, pidx: number) => (
                        <React.Fragment key={pidx}>
                          {renderPartner(
                            { partners: pos.partners, positionId: pos.positionId },
                            pos.name,
                            record.branchEntity,
                            pos.name,
                          )}
                        </React.Fragment>
                      ))}
                    </Card>
                  </Col>
                );
              })
            ) : (
              <Col span={24}>
                <Empty className="mt-5" description="No hay datos" />
              </Col>
            )}
          </Row>
        )}
      </div>

      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        width={400}
        styles={{ body: { padding: 0 } }}
      >
        <div className="p-4">
          <AntTitle level={5} className="mb-0">
            Reasignar Puesto
          </AntTitle>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {selectedSpot?.positionName} ·{" "}
            <span className="fw-bold">{selectedSpot?.branch?.name}</span>
          </Text>

          <Divider className="my-3" />

          <Space direction="vertical" className="w-100" size={12}>
            <div>
              <Text
                strong
                className="d-block mb-1"
                style={{ fontSize: "12px" }}
              >
                Colaborador:
              </Text>
              <div ref={dropdownRef} className="position-relative">
                <Input
                  size="small"
                  prefix={
                    searchLoading ? (
                      <SearchOutlined spin />
                    ) : (
                      <SearchOutlined className="text-muted" />
                    )
                  }
                  placeholder="Buscar..."
                  value={searchPartner}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  className="rounded-2 py-1"
                />

                {showDropdown && filteredPartners.length > 0 && (
                  <div className="autocomplete-dropdown shadow-lg rounded-2 border bg-white mt-1">
                    {filteredPartners.map((p) => (
                      <div
                        key={p.id}
                        className="p-2 dropdown-item d-flex align-items-center border-bottom"
                        onClick={() => {
                          setSelectedPartner(p);
                          setSearchPartner(
                            `${p.person.firstName} ${p.person.lastName}`,
                          );
                          setShowDropdown(false);
                        }}
                      >
                        <Avatar
                          size={24}
                          src={p.urlImage}
                          icon={<UserOutlined />}
                          className="me-2 rounded-1"
                        />
                        <div
                          className="d-flex flex-column"
                          style={{ minWidth: 0 }}
                        >
                          <Text
                            strong
                            style={{ fontSize: "11px" }}
                            className="text-truncate"
                          >
                            {p.person.firstName} {p.person.lastName}
                          </Text>
                          <Text
                            type="secondary"
                            style={{ fontSize: "10px" }}
                            className="text-truncate"
                          >
                            {p.candidate?.vacancy?.position?.name}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedPartner && (
              <div className="bg-blue-50 p-2 rounded-2 border border-blue-100">
                <Space size={4}>
                  <InfoCircleOutlined
                    className="text-primary"
                    style={{ fontSize: "12px" }}
                  />
                  <Text style={{ fontSize: "11px" }}>
                    Se moverá a <b>{selectedPartner.person.firstName}</b>
                  </Text>
                </Space>
              </div>
            )}

            <div>
              <Text
                strong
                className="d-block mb-1"
                style={{ fontSize: "12px" }}
              >
                Motivo:
              </Text>
              <Input.TextArea
                rows={2}
                size="small"
                className="rounded-2"
                placeholder="Motivo..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div className="d-flex justify-content-end gap-2 mt-2">
              <Button
                size="small"
                onClick={() => setIsModalOpen(false)}
                className="rounded-2"
                disabled={savingChange}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                size="small"
                className="rounded-2"
                loading={savingChange}
                onClick={handleSaveChange}
                style={{ background: "#4A90E2", borderColor: "#4A90E2" }}
              >
                Confirmar
              </Button>
            </div>
          </Space>
        </div>
      </Modal>

      <style>
        {`
          .card-compact {
            transition: transform 0.3s ease;
            background: #ffffff;
            border: 1px solid rgba(0,0,0,0.05) !important;
          }
          .card-compact:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 24px rgba(0,0,0,0.08) !important;
          }
          .bg-light-gray {
            background-color: #f8fafc;
          }
          .role-section {
             background: #f9fafb;
             border: 1px solid #f1f5f9;
          }
          .hover-scale {
            transition: transform 0.2s;
          }
          .hover-scale:hover {
            transform: scale(1.15);
          }
          .autocomplete-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 2000;
            max-height: 200px;
            overflow-y: auto;
          }
          .dropdown-item {
            cursor: pointer;
          }
          .dropdown-item:hover {
            background-color: #f0f7ff;
          }
          .bg-blue-50 {
            background-color: #f0f7ff;
          }
          .border-blue-100 {
            border-color: #e0efff;
          }
          .ant-card-head {
            border-bottom: 1px solid #f1f5f9 !important;
            min-height: 48px !important;
            padding: 0 12px !important;
          }
          .ant-card-head-title {
            padding: 12px 0 !important;
          }
          .ant-card-body {
            padding: 12px !important;
          }
        `}
      </style>
    </Layout>
  );
};

export default PlantillaLideres;
